import { useRef, useState, ReactNode, ChangeEvent, DragEvent } from "react";

export type FileUploaderError = {
  type: "size" | "type" | "custom";
  message: string;
};

export type Props = {
  /** Name attribute for the file input */
  name: string;
  /** Accepted file types (e.g., "image/*", ".pdf,.doc") */
  accept?: string;
  /** Maximum file size in bytes */
  maxSize?: number;
  /** Whether multiple files can be selected */
  multiple?: boolean;
  /** Whether the uploader is disabled */
  disabled?: boolean;
  /** File change handler */
  onChange?: (files: File[]) => void;
  /** Error handler */
  onError?: (error: FileUploaderError) => void;
  /** Additional CSS classes */
  className?: string;
  /** Custom dropzone renderer */
  renderDropzone?: (props: {
    isDragActive: boolean;
    disabled?: boolean;
    onClick: () => void;
    onDragOver: (e: DragEvent) => void;
    onDragLeave: (e: DragEvent) => void;
    onDrop: (e: DragEvent) => void;
  }) => ReactNode;
  /** Custom file input renderer (hidden by default) */
  renderInput?: (props: {
    ref: React.RefObject<HTMLInputElement>;
    name: string;
    accept?: string;
    multiple?: boolean;
    disabled?: boolean;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  }) => ReactNode;
  /** Accessibility label */
  "aria-label"?: string;
  /** Test ID for testing */
  "data-testid"?: string;
};

/**
 * Base file uploader component with no styling.
 * Handles drag and drop, file validation, and accessibility.
 * Use render props to customize appearance.
 *
 * @example
 * // In shared/ui/FileUploader.tsx
 * import { FileUploaderBase, type Props as FileUploaderBaseProps } from "@core/features/uploadFile";
 *
 * export function FileUploader({ className, ...props }: FileUploaderBaseProps) {
 *   return (
 *     <FileUploaderBase
 *       {...props}
 *       renderDropzone={({ isDragActive, onClick, onDragOver, onDragLeave, onDrop }) => (
 *         <div
 *           onClick={onClick}
 *           onDragOver={onDragOver}
 *           onDragLeave={onDragLeave}
 *           onDrop={onDrop}
 *           className={twMerge("border-2 border-dashed p-8", isDragActive && "border-blue-500")}
 *         >
 *           Drop files here or click to upload
 *         </div>
 *       )}
 *     />
 *   );
 * }
 */
export function FileUploaderBase({
  name,
  accept,
  maxSize,
  multiple = false,
  disabled = false,
  onChange,
  onError,
  className,
  renderDropzone,
  renderInput,
  "aria-label": ariaLabel = "Upload file",
  "data-testid": testId = "file-uploader",
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  const validateFile = (file: File): FileUploaderError | null => {
    // Validate file size
    if (maxSize && file.size > maxSize) {
      return {
        type: "size",
        message: `File size exceeds maximum allowed size of ${formatBytes(maxSize)}`,
      };
    }

    // Validate file type if accept is specified
    if (accept) {
      const acceptedTypes = accept.split(",").map((t) => t.trim());
      const fileType = file.type;
      const fileName = file.name;
      const fileExtension = "." + fileName.split(".").pop()?.toLowerCase();

      const isAccepted = acceptedTypes.some((acceptedType) => {
        if (acceptedType.startsWith(".")) {
          // Extension match
          return fileExtension === acceptedType.toLowerCase();
        } else if (acceptedType.endsWith("/*")) {
          // Wildcard MIME type (e.g., "image/*")
          const typePrefix = acceptedType.replace("/*", "");
          return fileType.startsWith(typePrefix);
        } else {
          // Exact MIME type match
          return fileType === acceptedType;
        }
      });

      if (!isAccepted) {
        return {
          type: "type",
          message: `File type not accepted. Accepted types: ${accept}`,
        };
      }
    }

    return null;
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const validFiles: File[] = [];

    for (const file of fileArray) {
      const error = validateFile(file);
      if (error) {
        onError?.(error);
        return; // Stop on first error
      }
      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      onChange?.(validFiles);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    // Reset input value to allow selecting the same file again
    e.target.value = "";
  };

  const handleClick = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (disabled) return;

    const files = e.dataTransfer?.files;
    if (files) {
      // If not multiple, only take the first file
      if (!multiple && files.length > 1) {
        const singleFileList = new DataTransfer();
        singleFileList.items.add(files[0]);
        handleFiles(singleFileList.files);
      } else {
        handleFiles(files);
      }
    }
  };

  // Default input rendering (hidden)
  const defaultInput = (
    <input
      ref={inputRef}
      type="file"
      name={name}
      accept={accept}
      multiple={multiple}
      disabled={disabled}
      onChange={handleInputChange}
      aria-label={ariaLabel}
      style={{
        position: "absolute",
        width: 1,
        height: 1,
        padding: 0,
        margin: -1,
        overflow: "hidden",
        clip: "rect(0, 0, 0, 0)",
        whiteSpace: "nowrap",
        border: 0,
      }}
      data-file-input
    />
  );

  // Default dropzone rendering
  const defaultDropzone = (
    <div
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-disabled={disabled}
      data-dropzone
      data-drag-active={isDragActive}
    >
      {isDragActive ? "Drop files here" : "Click or drag files to upload"}
    </div>
  );

  return (
    <div className={className} data-testid={testId}>
      {renderInput
        ? renderInput({
            ref: inputRef,
            name,
            accept,
            multiple,
            disabled,
            onChange: handleInputChange,
          })
        : defaultInput}
      {renderDropzone
        ? renderDropzone({
            isDragActive,
            disabled,
            onClick: handleClick,
            onDragOver: handleDragOver,
            onDragLeave: handleDragLeave,
            onDrop: handleDrop,
          })
        : defaultDropzone}
    </div>
  );
}

FileUploaderBase.displayName = "FileUploaderBase";

// Helper function to format bytes
function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

